/**
 * Геометрия каналов — одна на рисование, попадание курсором и опоры.
 *
 * Раньше канал рисовался одной геометрией, а ловился другой: ломаной по
 * трём точкам. Вторая линия канала в эту ломаную не входила, и щелчок по
 * её правому концу или середине мимо фигуры проходил мимо — кружки там
 * были, а тянуть за них было нельзя.
 *
 * Все построения здесь аффинные — сдвиги и пропорции, — поэтому одна и
 * та же функция работает и на экране (x, y в пикселях), и в данных
 * фигуры (x — время, y — цена).
 *
 * Как в привычных терминалах:
 * — параллельный канал: основная линия по двум точкам, вторая — её копия,
 *   сдвинутая на третью точку;
 * — плоский верх/низ: наклонная линия по двум точкам и горизонталь на
 *   цене третьей точки под ней или над ней;
 * — расходящийся канал: две зеркальные линии — насколько одна идёт
 *   вверх, настолько другая вниз, — с общими левым и правым краем.
 */

export interface XY {
  readonly x: number;
  readonly y: number;
}

export type ChannelKind = 'parallel-channel' | 'flat-channel' | 'disjoint-channel';

export const CHANNEL_KINDS: ReadonlySet<string> = new Set<ChannelKind>([
  'parallel-channel',
  'flat-channel',
  'disjoint-channel',
]);

export function isChannel(kind: string): kind is ChannelKind {
  return CHANNEL_KINDS.has(kind);
}

export interface Channel {
  /** Основная линия: слева направо, как её поставили. */
  readonly first: readonly [XY, XY];
  /** Вторая линия: её левый конец под левым концом первой, правый — под правым. */
  readonly second: readonly [XY, XY];
}

/** Высота линии в точке x. */
export function lineAt(a: XY, b: XY, x: number): number {
  return b.x === a.x ? a.y : a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x);
}

/**
 * Правый конец второй линии расходящегося канала.
 *
 * Линии зеркальны относительно горизонтали посередине: у обоих краёв
 * середина между линиями одна и та же. Отсюда правый конец второй линии
 * всегда вычисляется из трёх точек — поэтому он и нарисован квадратиком.
 */
function disjointEnd(a: XY, b: XY, c: XY): XY {
  return { x: b.x, y: a.y + c.y - b.y };
}

export function channelOf(kind: string, points: ReadonlyArray<XY | null | undefined>): Channel | null {
  const [a, b, c] = points;
  if (!a || !b || !c) return null;
  switch (kind) {
    case 'parallel-channel': {
      const shift = c.y - lineAt(a, b, c.x);
      return { first: [a, b], second: [{ x: a.x, y: a.y + shift }, { x: b.x, y: b.y + shift }] };
    }
    case 'flat-channel':
      return { first: [a, b], second: [{ x: a.x, y: c.y }, { x: b.x, y: c.y }] };
    case 'disjoint-channel':
      return { first: [a, b], second: [{ x: a.x, y: c.y }, disjointEnd(a, b, c)] };
    default:
      return null;
  }
}

/** Середины линий: у параллельного канала за них раздвигают стороны. */
function middle(p: XY, q: XY): XY {
  return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
}

/**
 * Опоры канала на экране, по порядку номеров.
 *
 * Параллельный — шесть: углы первой линии, углы второй, середины обеих.
 * Плоский и расходящийся — четыре угла.
 */
export function channelHandles(kind: string, channel: Channel): XY[] {
  const [a, b] = channel.first;
  const [c, d] = channel.second;
  if (kind === 'parallel-channel') return [a, b, c, d, middle(a, b), middle(c, d)];
  return [a, b, c, d];
}

/** Контур канала — для заливки и для попадания внутрь. */
export function channelOutline(channel: Channel): XY[] {
  return [channel.first[0], channel.first[1], channel.second[1], channel.second[0]];
}

/**
 * Новые точки канала, когда тянут его опору (x — время, y — цена).
 *
 * Параллельный:
 * — углы первой линии двигают свой конец, вторая идёт следом, ширина та же;
 * — углы второй линии двигают свой край канала целиком: обе линии вместе;
 * — середина первой двигает первую линию, вторая стоит на месте;
 * — середина второй двигает только вторую — канал раздвигается.
 *
 * Плоский верх/низ:
 * — концы наклонной линии — как у обычной линии;
 * — концы горизонтали меняют её цену, а по времени сдвигают свой край
 *   всего канала: левый — левый, правый — правый.
 *
 * Расходящийся (линии зеркальны, середина между ними — горизонталь):
 * — левые кружки, верхний и нижний, одинаково разводят или сводят левые
 *   концы — симметрично, правые стоят;
 * — правый кружок первой линии так же разводит правые концы;
 * — квадратик на конце второй линии сдвигает её целиком: канал
 *   сужается или расширяется, наклоны те же.
 * По времени левые кружки двигают левый край, правые — правый.
 */
export function channelDragged(kind: string, points: readonly XY[], handle: number, p: XY): XY[] | null {
  const [a, b, c] = points;
  if (!a || !b || !c) return null;

  if (kind === 'parallel-channel') {
    const shift = c.y - lineAt(a, b, c.x);
    const built = (na: XY, nb: XY, s: number): XY[] => [na, nb, { x: na.x, y: na.y + s }];
    switch (handle) {
      case 0:
        return built(p, b, shift);
      case 1:
        return built(a, p, shift);
      case 2:
        return built({ x: p.x, y: p.y - shift }, b, shift);
      case 3:
        return built(a, { x: p.x, y: p.y - shift }, shift);
      case 4: {
        const dy = p.y - (a.y + b.y) / 2;
        return built({ x: a.x, y: a.y + dy }, { x: b.x, y: b.y + dy }, shift - dy);
      }
      case 5:
        return built(a, b, p.y - lineAt(a, b, (a.x + b.x) / 2));
      default:
        return null;
    }
  }

  if (kind === 'flat-channel') {
    switch (handle) {
      case 0:
        return [p, b, { x: p.x, y: c.y }];
      case 1:
        return [a, p, { x: a.x, y: c.y }];
      case 2:
        return [{ x: p.x, y: a.y }, b, { x: p.x, y: p.y }];
      case 3:
        return [a, { x: p.x, y: b.y }, { x: a.x, y: p.y }];
      default:
        return null;
    }
  }

  if (kind === 'disjoint-channel') {
    // Середина между левыми концами — ось, от которой они расходятся.
    const axis = a.y + c.y;
    switch (handle) {
      case 0:
        return [p, { x: b.x, y: b.y }, { x: p.x, y: axis - p.y }];
      case 2:
        return [{ x: p.x, y: axis - p.y }, b, p];
      case 1:
        // Правый конец второй линии a + c − b: он уходит на столько же в другую сторону.
        return [a, p, c];
      case 3: {
        const d = disjointEnd(a, b, c);
        const dy = p.y - d.y;
        return [a, { x: p.x, y: b.y }, { x: c.x, y: c.y + dy }];
      }
      default:
        return null;
    }
  }

  return null;
}

/** Точка внутри многоугольника — по чётности пересечений. */
export function insidePolygon(x: number, y: number, polygon: readonly XY[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    if (pi.y > y !== pj.y > y && x < ((pj.x - pi.x) * (y - pi.y)) / (pj.y - pi.y) + pi.x) inside = !inside;
  }
  return inside;
}

/** Опора, которую не ставят, а вычисляют: рисуется квадратиком, как в привычных терминалах. */
export function isDerivedHandle(kind: string, index: number): boolean {
  return kind === 'disjoint-channel' && index === 3;
}
